import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './App.css';
import PasswordGate from './components/PasswordGate';
import MainPage from './components/MainPage';
import SurveyPage from './components/SurveyPage';
import ResultPage from './components/ResultPage';
import { SYMPTOMS } from './data/symptoms';
import { SLEEP_QUESTIONS } from './data/sleepQuestions';
import { WEIGHT_QUESTIONS } from './data/weightQuestions';
import { DIGESTION_QUESTIONS } from './data/digestionQuestions';
import { IMMUNITY_QUESTIONS } from './data/immunityQuestions';
import { MALE_QUESTIONS } from './data/maleQuestions';
import { FEMALE_QUESTIONS } from './data/femaleQuestions';
import { HANGOVER_QUESTIONS } from './data/hangoverQuestions'; 

function App() {
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [currentPage, setCurrentPage] = useState('main');
  const [selectedSymptom, setSelectedSymptom] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [questionFlow, setQuestionFlow] = useState([]);
  const [darkMode, setDarkMode] = useState(false);

  // 检查是否已解锁
  useEffect(() => {
    const unlocked = sessionStorage.getItem('herbalzen_unlocked');
    if (unlocked === 'true') {
      setIsUnlocked(true);
    }
  }, []);

  const getQuestionsForSymptom = (symptomId) => {
    switch (symptomId) {
      case 'sleep':
        return SLEEP_QUESTIONS;
      case 'weight':
        return WEIGHT_QUESTIONS;
      case 'digestion':
        return DIGESTION_QUESTIONS;
      case 'immunity':  
        return IMMUNITY_QUESTIONS;
      case 'male':  
        return MALE_QUESTIONS;
      case 'female':  
        return FEMALE_QUESTIONS;
      case 'hangover':  // 添加这个case
        return HANGOVER_QUESTIONS;
      default:
        return SLEEP_QUESTIONS;
    }
  };

  useEffect(() => {
    if (currentPage === 'survey' && selectedSymptom) {
      const questions = getQuestionsForSymptom(selectedSymptom.id);
      const flow = [];
      let questionCounter = 0;

      Object.values(questions).forEach(q => {
        if (!q.condition || q.condition(answers)) {
          flow.push(q);
          questionCounter++;

          if (questionCounter === 2) {
            flow.push({
              id: 'assurance_1',
              type: 'assurance',
              title: 'Did you know?',
              text: 'Herbal tea is very beneficial to the human body, supporting natural healing and vitality.',
              icon: '🌿'
            });
          }

          if (questionCounter === 5) {
            flow.push({
              id: 'assurance_2',
              type: 'assurance',
              title: 'Fun Fact',
              text: '94% of our users reported better daily wellness after trying their perfectly matched formula.',
              icon: '✨'
            });
          }
        }
      });
      
      setQuestionFlow(flow);
    }
  }, [answers, currentPage, selectedSymptom]);

  const handleSymptomSelect = (symptom) => {
    if (!symptom.enabled) return;
    setSelectedSymptom(symptom);
    setCurrentPage('survey');
    setCurrentQuestionIndex(0);
    setAnswers({});
  };

  const handleAnswer = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleNext = () => {
    if (currentQuestionIndex < questionFlow.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      setCurrentPage('result');
    }
  };

  const handleBack = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    } else {
      setCurrentPage('main');
      setSelectedSymptom(null);
      setAnswers({});
    }
  };

  const handleStartOver = () => {
    setCurrentPage('main');
    setSelectedSymptom(null);
    setAnswers({});
    setCurrentQuestionIndex(0);
  };

  const toggleDarkMode = () => {
    setDarkMode(prev => !prev);
  };

  const currentQuestion = questionFlow[currentQuestionIndex];
  const progress = questionFlow.length > 0 ? ((currentQuestionIndex + 1) / questionFlow.length) * 100 : 0;

  const isCurrentQuestionAnswered = () => {
    if (!currentQuestion) return false;
    if (currentQuestion.type === 'assurance') return true;
    
    const answer = answers[currentQuestion.id];
    if (!answer) return false;

    if (currentQuestion.type === 'multi-part') {
      return currentQuestion.parts.every(part => answers[currentQuestion.id]?.[part.id]);
    }
    if (Array.isArray(answer)) {
      return answer.length > 0;
    }
    return true;
  };

  // 如果未解锁，显示密码页面
  if (!isUnlocked) {
    return <PasswordGate onUnlock={() => setIsUnlocked(true)} />;
  }

  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    in: { opacity: 1, y: 0 },
    out: { opacity: 0, y: -20 }
  };

  const pageTransition = {
    type: 'tween',
    ease: 'anticipate',
    duration: 0.5
  };

  return (
    <div className={`app-wrapper ${darkMode ? 'dark-mode' : ''}`}>
      {currentPage !== 'survey' && (
        <button className="dark-mode-toggle" onClick={toggleDarkMode}>
          {darkMode ? '☀️' : '🌙'}
        </button>
      )}

      <div className="container">
        <AnimatePresence mode="wait">
          {currentPage === 'main' && (
            <motion.div
              key="main"
              initial="initial"
              animate="in"
              exit="out"
              variants={pageVariants}
              transition={pageTransition}
              style={{ width: '100%', display: 'flex', flexDirection: 'column', flex: 1 }}
            >
              <MainPage
                symptoms={SYMPTOMS}
                onSymptomSelect={handleSymptomSelect}
              />
            </motion.div>
          )}

          {currentPage === 'survey' && (
            <motion.div
              key={`survey-${currentQuestionIndex}`}
              initial="initial"
              animate="in"
              exit="out"
              variants={pageVariants}
              transition={pageTransition}
              style={{ width: '100%', display: 'flex', flexDirection: 'column', flex: 1 }}
            >
              <SurveyPage
                symptom={selectedSymptom}
                question={currentQuestion}
                answers={answers}
                progress={progress}
                onAnswer={handleAnswer}
                onNext={handleNext}
                onBack={handleBack}
                isAnswered={isCurrentQuestionAnswered()}
                darkMode={darkMode}
                toggleDarkMode={toggleDarkMode}
              />
            </motion.div>
          )}

          {currentPage === 'result' && (
            <motion.div
              key="result"
              initial="initial"
              animate="in"
              exit="out"
              variants={pageVariants}
              transition={pageTransition}
              style={{ width: '100%', display: 'flex', flexDirection: 'column', flex: 1 }}
            >
              <ResultPage
                symptom={selectedSymptom}
                answers={answers}
                onStartOver={handleStartOver}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

export default App;